<template>
    <template v-if="isA">
        <a
            :href="href"
            :class="[baseClass, sizeClass]"
            :dusk="props.dusk"
            :disabled="disabled"
            :target="target"
            @click="click"
        >
            <span v-if="loading">
                <Loading :loading="loading" :size="props.size"></Loading>
            </span>
            <span class="inline-flex items-center">
                <slot></slot>
                <span
                    v-if="shortcut"
                    class="inline-flex items-center ml-2 pl-2 border-l border-current/40 opacity-80"
                    aria-hidden="true"
                >
                    <KbdShortcut :keys="shortcut" tone="inline" size="sm" />
                </span>
            </span>
        </a>
    </template>
    <template v-else-if="isLink">
        <inertia-link
            :href="href"
            :class="[baseClass, sizeClass]"
            :dusk="props.dusk"
            :disabled="disabled"
            @click="click"
        >
            <span v-if="loading">
                <Loading :loading="loading" :size="props.size"></Loading>
            </span>
            <span class="inline-flex items-center">
                <slot></slot>
                <span
                    v-if="shortcut"
                    class="inline-flex items-center ml-2 pl-2 border-l border-current/40 opacity-80"
                    aria-hidden="true"
                >
                    <KbdShortcut :keys="shortcut" tone="inline" size="sm" />
                </span>
            </span>
        </inertia-link>
    </template>
    <template v-else>
        <button :class="[baseClass, sizeClass]" :dusk="props.dusk" :disabled="disabled" @click="click">
            <span v-if="loading">
                <Loading :loading="loading" :size="props.size"></Loading>
            </span>
            <span class="inline-flex items-center">
                <slot></slot>
                <span
                    v-if="shortcut"
                    class="inline-flex items-center ml-2 pl-2 border-l border-current/40 opacity-80"
                    aria-hidden="true"
                >
                    <KbdShortcut :keys="shortcut" tone="inline" size="sm" />
                </span>
            </span>
        </button>
    </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { Ref } from 'vue';
import { ComponentSize } from '@src/Common/DataGrid/types';

import { Link as InertiaLink } from '@inertiajs/vue3';
import Loading from '@src/Common/Components/Loading.vue';

const emit = defineEmits<{
    (event: 'click', $event): void;
}>();

interface Props {
    color?: string;
    variant?: 'inline' | 'solid';
    href?: string | null;
    loading?: boolean;
    dusk?: string;
    a?: boolean;
    disabled?: boolean;
    size?: ComponentSize;
    target?: string;
    shortcut?: string;
}

const props = withDefaults(defineProps<Props>(), {
    color: 'default',
    href: null,
    loading: false,
    dusk: 'button',
    a: false,
    disabled: false,
    size: 'base',
    target: '_self',
    variant: 'solid',
});

const isLink: Ref<boolean> = computed((): boolean => props.href !== null);
const isA: Ref<boolean> = computed((): boolean => props.a === true);

const sizeClass: Ref<string> = computed((): string => {
    const sizes = {
        xs: 'btn--xs',
        sm: 'btn--sm',
        base: 'btn--base',
        lg: 'btn--lg',
        xl: 'btn--xl',
    };

    return sizes[props.size] ?? sizes.base;
});

const baseClass: Ref<string> = computed((): string => {
    const classes: string[] = ['btn--base'];

    switch (props.variant) {
        case 'inline':
            classes.push('');
            break;

        case 'solid':
            classes.push('btn--solid');
            break;
    }

    switch (props.color) {
        case 'primary':
            switch (props.variant) {
                case 'inline':
                    classes.push('btn--primary--inline');
                    break;

                case 'solid':
                    classes.push('btn--primary--solid');
                    classes.push(props.disabled ? 'disabled' : '');
                    break;
            }
            break;

        case 'brand':
            switch (props.variant) {
                case 'inline':
                    classes.push('btn--brand--inline');
                    break;

                case 'solid':
                    classes.push('btn--brand--solid');
                    classes.push(props.disabled ? 'disabled' : '');
                    break;
            }

            break;

        case 'secondary':
            switch (props.variant) {
                case 'inline':
                    classes.push('btn--secondary--inline');
                    break;

                case 'solid':
                    classes.push('btn--secondary--solid');
                    classes.push(props.disabled ? 'disabled' : '');
                    break;
            }
            break;

        case 'danger':
            switch (props.variant) {
                case 'inline':
                    classes.push('btn--danger--inline');
                    break;

                case 'solid':
                    classes.push('btn--danger--solid');
                    classes.push(props.disabled ? 'disabled' : '');
                    break;
            }

            break;

        case 'default':
            switch (props.variant) {
                case 'inline':
                    classes.push('btn--default--inline');
                    break;

                case 'solid':
                    classes.push('btn--default--solid');
                    classes.push(props.disabled ? 'disabled' : '');
                    break;
            }
            break;
    }

    return classes.join(' ');
});

const click = ($event): boolean => {
    if (props.disabled) {
        return false;
    }
    emit('click', $event);

    return true;
};
</script>
